import React, { useState } from "react";
import { Mail, Lock, User, FileText, Check, Star, Crown } from "lucide-react";
import { createUserWithEmailAndPassword,sendEmailVerification } from "firebase/auth";
import { ref, set } from "firebase/database";
import { auth, db } from "./firebase"; // ✅ ensure db is exported in firebase.js
import logo from './asset/logo.png'

export default function SignupPage({ onPageChange }) {
  const [signupForm, setSignupForm] = useState({ name: "", email: "", password: "" });
  const [selectedPlan, setSelectedPlan] = useState("Free");
  const [loading, setLoading] = useState(false);

  const plans = [
    { id: "free", name: "Free", price: "0₹", amount: 0, period: "forever", popular: false, features: ["Unlimited papers", "Basic formatting", "PDF export", "Email support"] },
    { id: "basic", name: "Basic ", price: "99₹", amount: 9900, period: "per month", popular: true, features: ["Unlimited papers", "Advanced formatting", "Priority support", "5 Plagiarism checker", "10 AI content generation", "Limited Team collaboration (up to 3 users)"] },
    { id: "premium", name: "Premium ", price: "499₹", amount: 49900, period: "per month", popular: false, features: ["Everything in Basic", "Unlimited team members", "Advanced analytics", "Custom templates", "Dedicated support", "Custom integrations", "27 Plagiarism checker", "50 AI content generation"] }
  ];

  // ✅ Handle input changes
  const handleChange = (field, value) => {
    setSignupForm((prev) => ({ ...prev, [field]: value }));
  };

  // ✅ Razorpay Checkout
const handleRazorpayPayment = async (plan) => {
  const options = {
    key: "rzp_live_RSaDpAz8xyD2Ok",
    amount: plan.amount,
    currency: "INR",
    name: "IEEE Paper Pro",
    description: `${plan.name} Plan Subscription`,
    image: "/logo192.png",
    handler: async function (response) {
      console.log("Payment success:", response);

      try {
        const userCredential = await createUserWithEmailAndPassword(auth, signupForm.email, signupForm.password);
        const user = userCredential.user;

        await set(ref(db, "users/" + user.uid), {
          name: signupForm.name,
          email: signupForm.email,
          plan: plan.id,
          paymentId: response.razorpay_payment_id,
          status: "active",
          joinDate: new Date().toISOString(),
          papers: 0,
        });

        alert("Payment successful and account created!");
        onPageChange("login");
      } catch (error) {
        console.error("Signup error:", error);
        alert(error.message);
      } finally {
        setLoading(false);
      }
    },
    modal: {
      ondismiss: function () {
        alert("Payment popup closed.");
        setLoading(false);
      },
    },
    prefill: {
      name: signupForm.name,
      email: signupForm.email,
    },
    theme: {
      color: "#2563eb",
    },
  };

  const rzp = new window.Razorpay(options);

  rzp.on("payment.failed", function (response) {
    alert("Payment failed. Please try again.");
    console.error(response.error);
    setLoading(false);
  });

  rzp.open();
};

  // ✅ Signup Flow
const handleSignup = async (e) => {
  e.preventDefault();
  setLoading(true);

  try {
    if (selectedPlan === "freemium" || selectedPlan === "free") {
      // FREE plan — skip Razorpay
      const userCredential = await createUserWithEmailAndPassword(auth, signupForm.email, signupForm.password);
      const user = userCredential.user;

      await sendEmailVerification(user);

      await set(ref(db, "users/" + user.uid), {
        name: signupForm.name,
        email: signupForm.email,
        plan: selectedPlan,
        status: "inactive", 
        joinDate: new Date().toISOString(),
        papers: 0,
      });

      await auth.signOut();
      alert(`Verification email sent to ${user.email}. Please check your inbox or SPAM.`);
      onPageChange("login");
    } else {
      // Paid plan — use Razorpay
      const plan = plans.find((p) => p.id === selectedPlan);
      await handleRazorpayPayment(plan);
    }
  } catch (error) {
    console.error("Signup error:", error);
    alert(error.message);
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-emerald-900 flex items-center justify-center px-4 py-8">
        
      <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8 w-full max-w-4xl shadow-2xl">
        <div className="text-center mb-8">
        
          <img
              src={logo} // replace with your image path
              alt="Description"
              
              className="w-40 h-40 text-white mx-auto mb-4"
            />
          <h2 className="text-4xl font-bold text-white mb-2">Create Account</h2>
          <p className="text-white/70">Choose your plan and start generating IEEE papers</p>
        </div>

        {/* Plan Selection */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold text-white text-center mb-6">Choose Your Plan</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`relative bg-white/5 backdrop-blur-lg border-2 rounded-2xl p-6 cursor-pointer transition-all duration-300 hover:scale-105 ${
                  selectedPlan === plan.id ? "border-blue-500 bg-blue-500/20" : "border-white/10 hover:border-white/30"
                } ${plan.popular ? "ring-2 ring-yellow-400" : ""}`}
                onClick={() => setSelectedPlan(plan.id)}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold px-4 py-1 rounded-full flex items-center gap-1">
                      <Star className="w-3 h-3 fill-current" /> MOST POPULAR
                    </div>
                  </div>
                )}
                {plan.id === "premium" && <Crown className="absolute -top-2 -right-2 w-6 h-6 text-yellow-400" />}
                <h4 className="text-xl font-bold text-white mb-2 text-center">{plan.name}</h4>
                <div className="flex items-baseline justify-center gap-1 mb-4">
                  <span className="text-3xl font-black text-white">{plan.price}</span>
                  <span className="text-white/60 text-sm">{plan.period}</span>
                </div>
                <ul className="space-y-2">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-white/80 text-sm">
                      <Check className="w-5 h-5 text-emerald-400 flex-shrink-0" /> {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Signup Form */}
        <form onSubmit={handleSignup} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Name */}
            <div>
              <label className="block text-white text-sm font-medium mb-2">Full Name *</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" />
                <input
                  type="text"
                  value={signupForm.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50"
                  placeholder="Your Name"
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-white text-sm font-medium mb-2">Email Address *</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" />
                <input
                  type="email"
                  value={signupForm.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50"
                  placeholder="your@email.com"
                  required
                />
              </div>
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-white text-sm font-medium mb-2">Password *</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" />
              <input
                type="password"
                value={signupForm.password}
                onChange={(e) => handleChange("password", e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50"
                placeholder="••••••••"
                required
                minLength="6"
              />
            </div>
          </div>

          {/* Submit */}
          <button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-blue-600 to-emerald-600 text-white py-4 rounded-xl font-bold">
            {loading ? "Processing..." : `Get Started with ${plans.find((p) => p.id === selectedPlan)?.name}`}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-white/70 text-sm">
            Already have an account?{" "}
            <button onClick={() => onPageChange("login")} className="text-white font-semibold hover:underline">
              Log in
            </button>
          </p>
          <button onClick={() => onPageChange("landing")} className="mt-4 text-white/70 text-sm hover:text-white transition">
            ← Back to home
          </button>
        </div>
      </div>
    </div>
  );
}
